import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles } from 'lucide-react';
import V6ModernPicker from '../shared/V6ModernPicker';
import { API_URL } from '../../config';

const RemixModal = ({ 
  isOpen, 
  onClose, 
  originalImage,
  originalPrompt,
  originalSettings,
  onRemix,
  isGenerating,
  readOnly = false
}) => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [remixPrompt, setRemixPrompt] = useState('');
  const [changeRequest, setChangeRequest] = useState(''); // NEU: Separates Änderungsfeld
  const [isAnalyzing, setIsAnalyzing] = useState(false); // NEU: Bild wird analysiert
  const [analyzedPrompt, setAnalyzedPrompt] = useState(''); // NEU: GPT-4 Vision Analyse
  const [isGerman, setIsGerman] = useState(false); // Toggle DE/EN
  const [isTranslating, setIsTranslating] = useState(false);
  const [numImages, setNumImages] = useState(4);
  const [timeOfDay, setTimeOfDay] = useState(originalSettings?.time_of_day || 'sunset');
  const [weather, setWeather] = useState(originalSettings?.weather || 'clear');
  const [camera, setCamera] = useState(originalSettings?.camera || '35mm');
  const [style, setStyle] = useState(originalSettings?.style || 'cinematic');
  const [substyle, setSubstyle] = useState(originalSettings?.substyle || 'epic');
  const [imageFormat, setImageFormat] = useState(originalSettings?.image_format || 'portrait_9_16');
  const [referenceStrength, setReferenceStrength] = useState(0.7);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false); // NEU: Templates Modal
  
  // 🔒 Body Scroll Lock für Modal (iOS Safari kompatibel!)
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.overflow = 'hidden';
      
      return () => {
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  // Lade Config aus Backend (V1 - kann später dynamisch gemacht werden)
  useEffect(() => {
    if (isOpen) {
      loadConfig();
      // Analyse NICHT automatisch starten - User entscheidet selbst
    }
  }, [isOpen]);
  
  // NEU: Bild mit GPT-4 Vision analysieren
  const analyzeImage = async () => {
    if (!originalImage) return;
    
    setIsAnalyzing(true);
    setAnalyzedPrompt('');
    
    try {
      console.log('🔍 Analyzing image for remix:', originalImage);
      const res = await fetch(`${API_URL}/api/reference-image/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: originalImage })
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('✅ Image analyzed:', data.description?.substring(0, 100));
        setAnalyzedPrompt(data.description || '');
      } else {
        console.error('❌ Analysis failed:', res.status);
        // Fallback zum Original-Prompt
        setAnalyzedPrompt(originalPrompt || '');
      }
    } catch (error) {
      console.error('❌ Analysis error:', error);
      setAnalyzedPrompt(originalPrompt || '');
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const loadConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/modes/public/remix/v1`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading remix config:', error);
      // Fallback zu Default-Werten
      setConfig({
        header_title: 'Bild remixen',
        header_subtitle: '🎬 Du liebst das Bild – willst aber eine andere Stimmung?',
        original_image_label: 'Original-Bild',
        original_image_badge_text: 'Referenz',
        original_prompt_label: 'Original Prompt',
        original_prompt_empty_text: 'Kein Prompt verfügbar',
        remix_prompt_label: 'Deine Änderungen hinzufügen',
        remix_prompt_hint: '💡 Das Original-Bild wird als Referenz verwendet. Die KI erstellt neue Variationen mit deinen Änderungen.',
        remix_prompt_placeholder: 'z.B. andere Location, dramatischeres Licht, anderer Kamerawinkel...',
        templates_button_text: '💡 Keine Idee? Nutze Vorlagen',
        settings_label: 'Einstellungen anpassen (Optional)',
        num_images_label: 'Anzahl Variationen',
        slider_label: 'Variation vs. Original',
        slider_hint_low: '50% = Viel Variation',
        slider_hint_mid: '70% = Standard',
        slider_hint_high: '95% = Fast identisch',
        slider_info_title: 'Wie funktioniert der Slider?',
        slider_info_items: [
          '50%: Viel Freiheit für die KI – große Änderungen möglich (Pose, Hintergrund)',
          '70%: Balance – Pose bleibt ähnlich, Details ändern sich',
          '95%: Minimal – nur kleine Anpassungen (Details, Farben, Licht)'
        ],
        button_cancel_text: 'Abbrechen',
        button_generate_text: '{count} Remix-{variant} erstellen',
        info_title: '🎬 REMIX = Komplett neue Szenen!',
        info_text: 'Remix erzeugt neue Varianten: anderer Ausdruck, neues Styling, frische Details oder komplett neuer Look. Gleiche Person, neue Möglichkeiten.',
        templates_modal_title: '💡 Remix-Vorlagen',
        templates_modal_subtitle: 'Vorlagen verfügbar',
        templates_info_text: '💡 Klicke auf eine Vorlage, um sie als Remix-Prompt zu verwenden. Du kannst sie danach noch anpassen.',
        templates: []
      });
    }
  };
  
  // Initialize remixPrompt with originalPrompt (editierbar!)
  useEffect(() => {
    if (isOpen && originalPrompt) {
      setRemixPrompt(originalPrompt);
    }
  }, [isOpen, originalPrompt]);
  


useEffect(() => { if (isOpen) { // Lock scroll 
    const scrollY = window.scrollY; 
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed'; 
    document.body.style.top = `-${scrollY}px`; 
    document.body.style.width = '100%'; // Cleanup (unlock scroll) 
    return () => { 
      document.body.style.overflow = ''; 
      document.body.style.position = ''; 
      document.body.style.width = ''; 
      document.body.style.top = ''; 
      // Restore scroll position 
      window.scrollTo(0, scrollY); 
    }; }
   }, [isOpen]);



  // Block body scroll when modal is open
  
  
  if (!isOpen) return null;
  if (!config) return null; // Warte bis Config geladen ist
  
  const handleRemix = () => {
    // Sende analysierte Beschreibung UND Änderungswunsch separat
    onRemix({
      prompt: analyzedPrompt || originalPrompt,  // GPT-4 Vision Analyse (oder Fallback)
      changeRequest: changeRequest?.trim(),       // Nur der Änderungswunsch
      sourceImage: originalImage,
      num_images: numImages,
      settings: {
        time_of_day: timeOfDay,
        weather: weather,
        camera: camera,
        style: style,
        substyle: substyle,
        image_format: imageFormat
      },
      referenceStrength: referenceStrength
    });
  };
  
  // Toggle DE/EN Translation (via Backend - Google Translator, kein Limit!)
  const handleToggleLanguage = async () => {
    if (isTranslating || !analyzedPrompt) return;
    
    setIsTranslating(true);
    
    try {
      const sourceLang = isGerman ? 'de' : 'en';
      const targetLang = isGerman ? 'en' : 'de';
      
      // Übersetze die Bild-Analyse
      const res = await fetch(`${API_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: analyzedPrompt,
          source_lang: sourceLang,
          target_lang: targetLang
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAnalyzedPrompt(data.translated_text);
        setIsGerman(!isGerman);
      }
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
    }
  };
  
  // Button-Text dynamisch generieren
  const generateButtonText = () => {
    const variant = numImages === 1 ? 'Variation' : 'Variationen';
    return config.button_generate_text
      .replace('{count}', numImages)
      .replace('{variant}', variant);
  };
  
  // Don't render if not open
  if (!isOpen) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0, 0, 0, 0.98)', backdropFilter: 'blur(20px)', touchAction: 'none' }}
      onClick={onClose}
      onTouchStart={(e) => {
        // Blockiere Touch am Overlay (nicht im Modal-Content)
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onTouchMove={(e) => {
        // Blockiere alle Touch-Events am Overlay
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        className="relative max-w-4xl w-full h-full sm:h-auto bg-black border-0 sm:border-2 border-emerald-500/30 rounded-none sm:rounded-2xl p-6 glow-green-soft max-h-full sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur border border-emerald-500/30 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{config.header_title}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-white/30 text-white hover:bg-white/10 transition-all"
            data-testid="remix-modal-close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Original Image Preview */}
        <div>
          <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">{config.original_image_label}</h3>
          <div className="relative rounded-xl overflow-hidden border-2 border-white/20 max-w-md mx-auto">
            <img 
              src={originalImage} 
              alt="Original" 
              className="w-full h-auto"
            />
            <div className="absolute top-2 left-2 px-3 py-1.5 bg-[#10B981] text-black text-xs font-bold rounded-lg shadow-lg">
              {config.original_image_badge_text}
            </div>
          </div>
        </div>
            
            {/* Remix Prompt - EDITIERBAR mit Original vorausgefüllt */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold text-sm uppercase tracking-wider">Beschreibung bearbeiten</h3>
                {/* DE/EN Toggle */}
                <button
                  onClick={handleToggleLanguage}
                  disabled={isTranslating}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all disabled:opacity-50"
                  title="Sprache wechseln"
                >
                  {isTranslating ? (
                    <span className="text-xs text-white/70">...</span>
                  ) : (
                    <>
                      <span className="text-sm">{isGerman ? '🇩🇪' : '🇬🇧'}</span>
                      <span className="text-xs text-white/70">{isGerman ? 'DE' : 'EN'}</span>
                    </>
                  )}
                </button>
              </div>
              {/* Bild-Analyse (GPT-4 Vision) - EDITIERBAR! */}
              <div className="mb-4">
                <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block flex items-center justify-center gap-2">
                  🔍 Bild-Analyse
                  {isAnalyzing && <span className="text-[#10B981] animate-pulse text-[10px]">(wird analysiert...)</span>}
                  {!isAnalyzing && analyzedPrompt && <span className="text-yellow-500 text-[10px]">✏️ editierbar</span>}
                </label>
                {isAnalyzing ? (
                  <div className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white/70 text-sm flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-[#10B981]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    KI analysiert das Bild...
                  </div>
                ) : !analyzedPrompt ? (
                  <button
                    onClick={analyzeImage}
                    className="w-full px-4 py-3 bg-[#10B981]/20 border border-[#10B981]/50 rounded-xl text-[#10B981] text-sm font-medium hover:bg-[#10B981]/30 transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Bild analysieren (optional)
                  </button>
                ) : (
                  <textarea
                    value={analyzedPrompt}
                    onChange={(e) => setAnalyzedPrompt(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-yellow-500/30 rounded-xl text-white/80 text-sm resize-none focus:border-yellow-500/50 focus:outline-none"
                    rows={5}
                    placeholder="Bild-Analyse wird geladen..."
                  />
                )}
                {analyzedPrompt && (
                  <p className="text-yellow-500/70 text-[10px] mt-2 text-center">
                    ⚠️ Falls falsch (z.B. Haarfarbe), hier korrigieren
                  </p>
                )}
              </div>
              
              {/* Änderungswunsch (editierbar) */}
              <div>
                <label className="text-[#10B981] text-xs uppercase tracking-wider mb-2 block font-semibold">
                  ✨ Was möchtest du ändern?
                </label>
                <textarea
                  value={changeRequest}
                  onChange={(e) => setChangeRequest(e.target.value)}
                  placeholder="z.B. rote Haare, Sonnenbrille auf, eleganteres Outfit, anderer Hintergrund..."
                  className="w-full px-4 py-3 bg-white/5 backdrop-blur-sm border-2 border-[#10B981]/50 rounded-xl text-white placeholder:text-white/40 focus:border-[#10B981] focus:outline-none transition-colors duration-150 resize-none shadow-lg"
                  rows={3}
                  data-testid="remix-change-input"
                  disabled={isAnalyzing}
                />
                <p className="text-white/50 text-xs mt-2">
                  💡 Deine Änderung wird in die Bild-Analyse eingebaut
                </p>
              </div>
            </div>
            
            {/* Anzahl Bilder */}
            <div className="mt-8">
              <h3 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">{config.num_images_label}</h3>
              <div className="flex items-center justify-between p-4 bg-white/5 backdrop-blur border border-white/10 rounded-xl">
                <label className="text-sm font-medium text-gray-300">{config.num_images_question}</label>
                <select
                  value={numImages}
                  onChange={(e) => setNumImages(parseInt(e.target.value))}
                  className="px-5 py-2.5 bg-white/10 backdrop-blur border border-white/20 rounded-full text-white font-medium focus:border-#10B981/50 focus:outline-none transition-colors duration-150 cursor-pointer"
                >
                  {(config.num_images_options || [1, 2, 3, 4]).map(num => (
                    <option key={num} value={num} className="bg-[#1A1A1A]">{num} {num === 1 ? 'Bild' : 'Bilder'}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Hinweis: Mind. 2 Bilder empfohlen */}
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <p className="text-amber-300 text-sm flex items-center gap-2">
                <span>💡</span>
                <span><strong>Tipp:</strong> Wir empfehlen mindestens 2 Bilder zu generieren für beste Ergebnisse.</span>
              </p>
            </div>
            
            {/* Generate Button - ReadOnly leitet zu /welcome */}
            <button
              onClick={readOnly ? () => { navigate('/welcome'); setTimeout(() => window.scrollTo(0, 0), 100); } : handleRemix}
              disabled={!readOnly && (isGenerating || isAnalyzing || !changeRequest?.trim())}
              className={`mt-8 w-full py-5 px-6 rounded-full transition-all duration-200 font-bold text-lg flex items-center justify-center gap-3 ${
                (!readOnly && (isGenerating || isAnalyzing || !changeRequest?.trim()))
                  ? 'bg-white/10 text-white/40 cursor-not-allowed border border-white/10'
                  : 'bg-gradient-to-r from-[#10B981] via-[#10B981] to-[#059669] text-black hover:shadow-2xl hover:shadow-[#10B981]/60 hover:scale-[1.02] shadow-xl shadow-[#10B981]/50 border-2 border-[#10B981]/50'
              }`}
              data-testid="remix-generate-button"
            >
              {!readOnly && isGenerating ? (
                <>
                  <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{config.button_generating_text}</span>
                </>
              ) : readOnly ? (
                <>
                  <Sparkles className="w-6 h-6 drop-shadow-[0_0_6px_rgba(0,0,0,0.5)]" />
                  <span>Registrieren zum Nutzen</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6 drop-shadow-[0_0_6px_rgba(0,0,0,0.5)]" />
                  <span>{generateButtonText()}</span>
                </>
              )}
            </button>
            
            {/* Info */}
            <div className="mt-8 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-emerald-400 text-sm text-center font-semibold mb-2">
                {config.info_title}
              </p>
              <p className="text-emerald-300/80 text-xs text-center">
                {config.info_text}
              </p>
            </div>
      </div>
      
    </div>,
    document.body
  );
};

export default RemixModal;
